import { useMemo } from "react";
import { useGetSeriesBarChartPaymentByData } from "@/modules/finance/helper/hooks/use-payment-statistical-view";
import { moneyFormat, numberFormat } from "@/helper/format-data";
import { usePaymentHistoryStatistic } from "./use-payment-history-statistic";
import { useOrdersSummary } from "@/modules/finance/helper/hooks/use-orders-summary";
import { makeSubDateRange } from "@/helper/date/make-sub-date-range.util";
import { usePaymentMethodsDistribution } from "./use-payment-methods-distribution";
import { percentVariation } from "@/helper/percent-variation.util";
import { useParkingRevenueChartOption } from "@/modules/finance/helper/hooks/use-parking-revenue-chart-option";
import { useUserProfilesDistribution } from "./use-profiles-distribution";
import { usePaymentZonesDistribution } from "./use-payment-zone-distribution";
import { getUserProfilesChart } from "@/helper/chart/user-profiles-chart.util";
import { getPaymentZonesChart } from "@/helper/chart/payment-zones-chart.util";
import { getPaymentMethodsChart } from "@/helper/chart/payment-methods-chart.util";

export const useAppTabState = (cityId: string | undefined | null, startDate: Date, endDate: Date) => {
  const { startDate: newStartDate, endDate: newEndDate } = makeSubDateRange(startDate, endDate);

  // Payment Methods Distribution
  const { data: paymentMethodDistribution = [], loading: loadingPaymentMethodDistribution } =
    usePaymentMethodsDistribution(cityId, startDate, endDate);

  const paymentMethodPieChartData = useMemo(
    () =>
      paymentMethodDistribution && getPaymentMethodsChart(paymentMethodDistribution, { title: { showLabel: false } }),
    [paymentMethodDistribution],
  );

  // Zone names Distribution
  const { data: zoneNameDistribution = [], loading: loadingZoneNameDistribution } = usePaymentZonesDistribution(
    cityId,
    startDate,
    endDate,
  );

  const zoneNamePieChartData = useMemo(
    () => zoneNameDistribution && getPaymentZonesChart(zoneNameDistribution),
    [zoneNameDistribution],
  );

  // User profiles Distribution
  const { data: userProfilesDistribution = [], loading: loadingUserProfilesDistribution } = useUserProfilesDistribution(
    cityId,
    startDate,
    endDate,
  );

  const userProfilesPieChartData = useMemo(
    () => userProfilesDistribution && getUserProfilesChart(userProfilesDistribution),
    [userProfilesDistribution],
  );

  const { paymentHistoryStatisticData, loading: paymentHistoryStatisticDataLoading } = usePaymentHistoryStatistic(
    cityId,
    startDate,
    endDate,
  );

  const { data: ordersSummary, loading: ordersSummaryLoading } = useOrdersSummary(cityId, startDate, endDate);

  const { data: ordersSummaryAgo, loading: ordersSummaryAgoLoading } = useOrdersSummary(
    cityId,
    newStartDate,
    newEndDate,
  );

  const { data = [], xAxis = [] } =
    useGetSeriesBarChartPaymentByData({
      paymentHistoryStatisticData: paymentHistoryStatisticData.data,
      startDate,
      endDate,
    }) ?? {};

  const summaryChartOptions = useParkingRevenueChartOption(xAxis, data);

  const averageAmountPerTransaction = useMemo(() => {
    if (!ordersSummary || !ordersSummary.count) {
      return moneyFormat(0);
    }
    return moneyFormat(ordersSummary.amount / ordersSummary.count / 100);
  }, [ordersSummary]);

  const amountVariation = useMemo(() => {
    return percentVariation(ordersSummary?.amount, ordersSummaryAgo?.amount);
  }, [ordersSummary, ordersSummaryAgo]);

  const transactionVariation = useMemo(() => {
    return percentVariation(ordersSummary?.count, ordersSummaryAgo?.count);
  }, [ordersSummary, ordersSummaryAgo]);

  const averageAmountPerTransactionVariation = useMemo(() => {
    if (!ordersSummary || !ordersSummaryAgo || ordersSummary.count === 0 || ordersSummaryAgo.count === 0) {
      return 0;
    }
    const averageCurrent = ordersSummary.amount / ordersSummary.count;
    const averageAgo = ordersSummaryAgo.amount / ordersSummaryAgo.count;
    return percentVariation(averageCurrent, averageAgo);
  }, [ordersSummary, ordersSummaryAgo]);

  const activeUsersVariation = useMemo(() => {
    return percentVariation(ordersSummary?.active_users_count, ordersSummaryAgo?.active_users_count);
  }, [ordersSummary, ordersSummaryAgo]);

  const totalAmount = useMemo(() => {
    return ordersSummary ? moneyFormat(ordersSummary.amount / 100) : moneyFormat(0);
  }, [ordersSummary]);

  return {
    loading:
      loadingPaymentMethodDistribution ||
      loadingZoneNameDistribution ||
      loadingUserProfilesDistribution ||
      paymentHistoryStatisticDataLoading ||
      ordersSummaryLoading ||
      ordersSummaryAgoLoading,
    paymentMethodPieChartData,
    zoneNamePieChartData,
    userProfilesPieChartData,
    averageAmountPerTransaction,
    amountVariation,
    transactionVariation,
    averageAmountPerTransactionVariation,
    activeUsersVariation,
    totalAmount,
    numberOfTransactions: numberFormat(ordersSummary?.count) || "0",
    activeUsers: numberFormat(ordersSummary?.active_users_count) || "0",
    summaryChartOptions,
  };
};
